/**
 * "Who we're looking for" — the organization header card (spec
 * docs/superpowers/specs/2026-09-18-org-looking-for-card-design.md).
 *
 * Pure: the shape stored at profile_data.lookingFor, the chips a card shows,
 * the viewer's private match, and the two editor nudges. No component, hook
 * or Supabase import, so every rule here is unit-tested directly.
 *
 * Two product rules shape this file and must not be relaxed:
 *
 * 1. NOTHING IS ASSUMED ON THE ORG'S BEHALF. An organization that has not said
 *    who it wants shows no requirements and no "open to everyone" line. That
 *    line appears only when the org ticked `openToAll` (Joe, 2026-09-18).
 *
 * 2. THE MATCH IS PRIVATE TO THE AGENT. `computeLookingForMatch` runs in the
 *    viewer's browser over their own profile row. Its result is never stored,
 *    sent, or attached to an analytics event.
 */
import {
  EXPERIENCE_MINIMUMS,
  MARKET_FOCUS_OPTIONS,
  SPECIALTY_OPTIONS,
  VOLUME_RANGES,
  TRANSACTION_RANGES,
  marketFocusLabel,
  transactionMinimumLabel,
  volumeMinimumLabel,
} from "@/lib/agentProductionOptions";
import { compensationGuardError } from "@/lib/compensationGuard";
import { officeLabelsAsStates, type OfficesDisplay } from "@/lib/officesDisplay";

export const LOOKING_FOR_MAX_CHIPS = 4;
export const LOOKING_FOR_HEADLINE_MAX = 80;
export const LOOKING_FOR_ANNOUNCEMENT_MAX = 90;
export const ANNOUNCEMENT_DEFAULT_DAYS = 30;
export const ANNOUNCEMENT_MAX_DAYS = 90;
/** The lengths an admin can pick for an announcement. */
export const ANNOUNCEMENT_DAY_CHOICES = [7, 14, 30, 60, 90] as const;
/** Days since the section was last saved before the editor asks "still accurate?". */
export const LOOKING_FOR_STALE_DAYS = 90;
/** Days before an announcement expires that the editor starts nudging. */
export const ANNOUNCEMENT_EXPIRING_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface LookingForRequirements {
  minYearsExperience?: number;
  minVolumeRange?: number;
  minTransactionRange?: number;
  marketFocus?: string[];
  specialties?: string[];
}

export interface LookingForAnnouncement {
  text: string;
  /** ISO timestamp; the announcement is hidden at and after this instant. */
  expiresAt: string;
  /** The length the admin picked, so the editor can show it as selected. */
  days?: number;
}

export interface LookingFor {
  headline?: string;
  /** Office labels ("City, ST"), a subset of the org's offices. */
  markets?: string[];
  /** The org said, explicitly, that it is open to agents at every stage. */
  openToAll?: boolean;
  requirements?: LookingForRequirements;
  announcement?: LookingForAnnouncement | null;
  /** ISO timestamp of the last change or "still accurate" confirmation. */
  updatedAt?: string;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown, max: number): string | undefined => {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t ? t.slice(0, max) : undefined;
};

/**
 * Free text agents will read. The editor refuses compensation figures before
 * saving; this drops one that arrived by any other path (a Console edit, a
 * direct write), so the card cannot show it either way.
 */
const agentText = (v: unknown, max: number): string | undefined => {
  const t = str(v, max);
  return t && !compensationGuardError(t) ? t : undefined;
};

const strings = (v: unknown, allowed?: readonly string[]): string[] => {
  if (!Array.isArray(v)) return [];
  const out = v.filter((x): x is string => typeof x === "string" && x.trim() !== "").map((x) => x.trim());
  const filtered = allowed ? out.filter((x) => allowed.includes(x)) : out;
  return [...new Set(filtered)];
};

const indexIn = (v: unknown, length: number): number | undefined =>
  typeof v === "number" && Number.isInteger(v) && v >= 1 && v < length ? v : undefined;

const isoDate = (v: unknown): string | undefined =>
  typeof v === "string" && !Number.isNaN(Date.parse(v)) ? v : undefined;

/**
 * Whatever is stored at profile_data.lookingFor, reduced to values every
 * reader can trust: unknown option ids dropped, out-of-range minimums dropped,
 * text trimmed and capped. Anything unreadable becomes `null`.
 */
export const normalizeLookingFor = (raw: unknown): LookingFor | null => {
  if (!isObject(raw)) return null;
  const req = isObject(raw.requirements) ? raw.requirements : {};
  const years = req.minYearsExperience;
  const requirements: LookingForRequirements = {
    minYearsExperience: (EXPERIENCE_MINIMUMS as readonly number[]).includes(years as number) ? (years as number) : undefined,
    minVolumeRange: indexIn(req.minVolumeRange, VOLUME_RANGES.length),
    minTransactionRange: indexIn(req.minTransactionRange, TRANSACTION_RANGES.length),
    marketFocus: strings(req.marketFocus, MARKET_FOCUS_OPTIONS.map((o) => o.id)),
    specialties: strings(req.specialties, SPECIALTY_OPTIONS),
  };
  let announcement: LookingForAnnouncement | null = null;
  if (isObject(raw.announcement)) {
    const text = agentText(raw.announcement.text, LOOKING_FOR_ANNOUNCEMENT_MAX);
    const expiresAt = isoDate(raw.announcement.expiresAt);
    const days = (ANNOUNCEMENT_DAY_CHOICES as readonly number[]).includes(raw.announcement.days as number)
      ? (raw.announcement.days as number)
      : undefined;
    if (text && expiresAt) announcement = days ? { text, expiresAt, days } : { text, expiresAt };
  }
  return {
    headline: agentText(raw.headline, LOOKING_FOR_HEADLINE_MAX),
    markets: strings(raw.markets),
    openToAll: raw.openToAll === true,
    requirements,
    announcement,
    updatedAt: isoDate(raw.updatedAt),
  };
};

export type ChipKind = "experience" | "volume" | "transactions" | "marketFocus" | "specialty";

export interface LookingForChip {
  kind: ChipKind;
  /** The option value: a minimum index/years, a market-focus id, or a specialty label. */
  value: string | number;
  label: string;
}

/**
 * Every requirement as a chip, in a fixed order: experience, volume,
 * transactions, then market focus, then specialties. Not capped — the editor
 * counts these against LOOKING_FOR_MAX_CHIPS; renderers call `cardChips`.
 */
export const requirementChips = (req: LookingForRequirements | undefined): LookingForChip[] => {
  if (!req) return [];
  const chips: LookingForChip[] = [];
  if (req.minYearsExperience) {
    chips.push({ kind: "experience", value: req.minYearsExperience, label: `${req.minYearsExperience}+ years licensed` });
  }
  if (req.minVolumeRange) {
    chips.push({ kind: "volume", value: req.minVolumeRange, label: `${volumeMinimumLabel(req.minVolumeRange)} annual volume` });
  }
  if (req.minTransactionRange) {
    chips.push({ kind: "transactions", value: req.minTransactionRange, label: `${transactionMinimumLabel(req.minTransactionRange)} transactions a year` });
  }
  for (const id of req.marketFocus ?? []) chips.push({ kind: "marketFocus", value: id, label: marketFocusLabel(id) });
  for (const s of req.specialties ?? []) chips.push({ kind: "specialty", value: s, label: s });
  return chips;
};

/** What a card renders: at most four chips, and none when the org said it is open to all. */
export const cardChips = (lf: LookingFor | null): LookingForChip[] =>
  !lf || lf.openToAll ? [] : requirementChips(lf.requirements).slice(0, LOOKING_FOR_MAX_CHIPS);

/** The announcement, if one exists and has not expired at `now`. */
export const liveAnnouncement = (lf: LookingFor | null, now: Date = new Date()): LookingForAnnouncement | null => {
  const a = lf?.announcement;
  if (!a) return null;
  return Date.parse(a.expiresAt) > now.getTime() ? a : null;
};

/** True when the org has told agents anything: a headline, a requirement, or "open to all". */
export const hasLookingForContent = (lf: LookingFor | null): boolean =>
  !!lf && (!!lf.headline || lf.openToAll === true || requirementChips(lf.requirements).length > 0);

// ── Match ─────────────────────────────────────────────────────────────────

export type ChipResult = "met" | "unmet" | "unknown";

export interface LookingForMatch {
  results: { chip: LookingForChip; result: ChipResult }[];
  met: number;
  unknown: number;
  total: number;
}

/**
 * The agent's own answers, read from their profiles.profile_data. Zero and
 * empty count as UNANSWERED: the agent editor defaults both sliders and the
 * years field to 0, so a stored 0 cannot be told apart from never touched.
 * An agent who genuinely has 0 years therefore sees "unknown", never "unmet" —
 * the kinder of the two readings.
 */
const agentAnswer = (agent: unknown, chip: LookingForChip): ChipResult => {
  const pd = isObject(agent) ? agent : {};
  const basic = isObject(pd.basic) ? pd.basic : {};
  const production = isObject(pd.production) ? pd.production : {};
  const num = (v: unknown) => (typeof v === "number" && v > 0 ? v : typeof v === "string" && Number(v) > 0 ? Number(v) : null);
  const list = (v: unknown) => (Array.isArray(v) ? (v as unknown[]).filter((x) => typeof x === "string") as string[] : []);
  switch (chip.kind) {
    case "experience": {
      const n = num(basic.yearsExperience);
      return n == null ? "unknown" : n >= (chip.value as number) ? "met" : "unmet";
    }
    case "volume": {
      const n = num(production.volumeRange);
      return n == null ? "unknown" : n >= (chip.value as number) ? "met" : "unmet";
    }
    case "transactions": {
      const n = num(production.transactionRange);
      return n == null ? "unknown" : n >= (chip.value as number) ? "met" : "unmet";
    }
    case "marketFocus": {
      const l = list(production.marketFocus);
      return l.length === 0 ? "unknown" : l.includes(chip.value as string) ? "met" : "unmet";
    }
    case "specialty": {
      const l = list(production.specialties);
      return l.length === 0 ? "unknown" : l.includes(chip.value as string) ? "met" : "unmet";
    }
  }
};

/** The viewer's match against the chips a card shows. Browser-only; never persist the result. */
export const computeLookingForMatch = (chips: LookingForChip[], agentProfileData: unknown): LookingForMatch => {
  const results = chips.map((chip) => ({ chip, result: agentAnswer(agentProfileData, chip) }));
  return {
    results,
    met: results.filter((r) => r.result === "met").length,
    unknown: results.filter((r) => r.result === "unknown").length,
    total: results.length,
  };
};

export type MatchFooter =
  | { kind: "none" }
  | { kind: "match"; text: string; addDetails: boolean }
  | { kind: "addGoals"; text: string };

/** The footer line for a signed-in agent. */
export const matchFooter = (m: LookingForMatch): MatchFooter => {
  if (m.total === 0) return { kind: "none" };
  if (m.unknown === m.total) return { kind: "addGoals", text: "Add your goals to see your fit" };
  const text = m.met === 0 ? "See what they're looking for" : `You match ${m.met} of ${m.total}`;
  return { kind: "match", text, addDetails: m.unknown > 0 };
};

// ── Editor nudges ─────────────────────────────────────────────────────────

export type LookingForNudge =
  | { kind: "empty" }
  | { kind: "stale"; days: number }
  | { kind: "announcementExpired" }
  | { kind: "announcementExpiring"; days: number }
  | { kind: "done" };

/**
 * What the admin is asked about, most urgent first. An expired or expiring
 * announcement outranks the 90-day "still accurate?" question because it is
 * the one piece of the card with a date agents can read against today.
 */
export const lookingForNudge = (lf: LookingFor | null, now: Date = new Date()): LookingForNudge => {
  if (!hasLookingForContent(lf)) return { kind: "empty" };
  const a = lf!.announcement;
  if (a) {
    const left = Date.parse(a.expiresAt) - now.getTime();
    if (left <= 0) return { kind: "announcementExpired" };
    if (left <= ANNOUNCEMENT_EXPIRING_DAYS * DAY_MS) return { kind: "announcementExpiring", days: Math.max(1, Math.ceil(left / DAY_MS)) };
  }
  if (lf!.updatedAt) {
    const age = Math.floor((now.getTime() - Date.parse(lf!.updatedAt)) / DAY_MS);
    if (age >= LOOKING_FOR_STALE_DAYS) return { kind: "stale", days: age };
  }
  return { kind: "done" };
};

/** An ISO expiry `days` from `now`, clamped to 1..ANNOUNCEMENT_MAX_DAYS. */
export const announcementExpiry = (days: number, now: Date = new Date()): string => {
  const d = Math.min(ANNOUNCEMENT_MAX_DAYS, Math.max(1, Math.round(days)));
  return new Date(now.getTime() + d * DAY_MS).toISOString();
};

/** "Sep 12" for the card's "Updated" stamp. */
export const shortDate = (iso: string | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

/**
 * Markets for the card: the ones the org chose, else every office, capped at
 * three with the rest counted. When the org shows its footprint as states
 * only, the markets are named by state too, so no city reaches the card.
 */
export const cardMarkets = (
  lf: LookingFor | null,
  offices: string[],
  display: OfficesDisplay = "cities",
): { shown: string[]; more: number } => {
  const chosen = (lf?.markets ?? []).filter((m) => offices.length === 0 || offices.includes(m));
  const labels = chosen.length ? chosen : offices.filter((o) => o.trim() !== "");
  const all = display === "states" ? officeLabelsAsStates(labels) : labels;
  return { shown: all.slice(0, 3), more: Math.max(0, all.length - 3) };
};
